import React, { useEffect, useState } from 'react';
import {
  AvatarBadge,
  Box,
  Flex,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Tooltip,
  Image,
  Grid,
  GridItem,
} from '@chakra-ui/core';
import { AiOutlineLike, AiFillLike } from 'react-icons/ai';
import moment from 'moment-timezone';
import { RiMoneyDollarCircleFill } from 'react-icons/ri';
import { FaGraduationCap, FaUserTimes } from 'react-icons/fa';
import { MdOutlineAccountBox } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { ProfilePicture } from '../../common';
import colors from '../../../styles/colors';
import { useStoreState } from '../../../models/hooks';
import { RemoteConfigKey } from '../../../firebase/remoteConfig';
import { SelectedTab, useTopCandidatesContext } from './TopCandidatesContext';
import SeekerExperiencePresenter from '../../messaging/seeker/candidate/profile/seekerExperiencePresenter';
import SeekerEducationPresenter from '../../messaging/seeker/candidate/profile/seekerEducationPresenter';
import { Experience } from '../../messaging/seeker/candidate/profile/components/Experience';
import { Education } from '../../messaging/seeker/candidate/profile/components/Education';
import useEducationLevels from '../../../app/hooks/useEducationLevels';
import { ExperienceType, TopCandidateDataType } from './TopCandidateDataType';
import { getTranslatedValue } from '../../../utils/localizedString';
import useCandidate from '../../../app/components/useCandidate';
import { TopCandidateBookmark } from './TopCandidateBookmark';

export type CandidateViewProps = {
  topCandidate: TopCandidateDataType;
  handleContactRequest: (selectedCandidate: TopCandidateDataType) => void;
};

enum CandidateStatus {
  CONTACT_REQUEST_ACCEPTED = 'CONTACT_REQUEST_ACCEPTED',
  CONTACT_REQUESTED = 'CONTACT_REQUESTED',
  LIKED = 'LIKED',
}

export const CandidateView = ({ topCandidate, handleContactRequest }: CandidateViewProps): JSX.Element => {
  const [profileURL, setProfileURl] = useState<string>();
  const { t, i18n } = useTranslation();
  const educationLevels = useEducationLevels();
  const minutesBeforeInactivity = useStoreState((state) => state.remoteConfig[RemoteConfigKey.MINUTES_BEFORE_INACTIVITY]);

  const recentlyActive =
    moment(topCandidate.seeker.lastActivity).toDate() > moment().subtract(minutesBeforeInactivity, 'minute').toDate();
  const seekersProfilePictures = useStoreState((state) => state.messaging.seekersProfilePictures);
  const { tab, selectedTopCandidate, setSelectedTopCandidate, positionDocument } = useTopCandidatesContext();

  const candidateExperiences = (
    topCandidate.seeker?.experiences?.map((experience) => new SeekerExperiencePresenter(experience)) ?? []
  ).sort((x, y) => Number(y.current) - Number(x.current));
  const candidateEducations = topCandidate.seeker?.educations?.map((education) => new SeekerEducationPresenter(education)) ?? [];
  const degree = educationLevels.find((edu) => edu.id === topCandidate.education);

  const candidateId = `${positionDocument?.id}_${topCandidate?.seeker?.id}`;
  const selectedCandidate = useCandidate(candidateId);

  const getLimitedExperienceLabel = (): string => {
    const w = topCandidate.workExperience;
    if (!w || w === 0) return '--';
    if (w < 0.5) return t('businessManagement:topCandidate.limitedExperienceLessThan');
    if (w === 0.5) return t('businessManagement:topCandidate.limitedExperienceMonths', { value: 6 });
    if (w <= 0.585) return t('businessManagement:topCandidate.limitedExperienceMonths', { value: 7 });
    if (w <= 0.667) return t('businessManagement:topCandidate.limitedExperienceMonths', { value: 8 });
    if (w <= 0.75) return t('businessManagement:topCandidate.limitedExperienceMonths', { value: 9 });
    if (w <= 0.834) return t('businessManagement:topCandidate.limitedExperienceMonths', { value: 10 });
    if (w <= 0.917) return t('businessManagement:topCandidate.limitedExperienceMonths', { value: 11 });
    return '--';
  };

  useEffect(() => {
    const url = seekersProfilePictures[topCandidate.seeker.id];
    setProfileURl(url);
  }, [topCandidate, seekersProfilePictures]);

  const firstName = topCandidate.seeker.firstName.trim();
  const lastName = topCandidate.seeker.lastName.trim();

  const isKnockedOutCandidate =
    selectedCandidate?.candidate?.system?.shouldBeDismissed &&
    selectedCandidate?.candidate?.knockedOut &&
    !selectedCandidate?.candidate?.monetized &&
    !selectedCandidate?.candidate?.dismissed;

  return (
    <Grid
      templateColumns={{ base: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' }}
      gap={4}
      borderRadius="8px"
      cursor="pointer"
      background={
        // eslint-disable-next-line no-nested-ternary
        isKnockedOutCandidate
          ? colors.red[50]
          : selectedTopCandidate?.seeker?.id === topCandidate.seeker.id
          ? '#2FA1DA1A'
          : colors.neutral[50]
      }
      p="16px"
      mt="12px"
      mr="12px"
      ml="12px"
      _hover={{
        transition: '0.09s ease-in transform',
      }}
      onClick={() => {
        setSelectedTopCandidate(topCandidate);
      }}
    >
      <GridItem>
        <Flex alignItems="center" mb={{ base: 4, md: 0 }}>
          <Flex alignItems="center">
            {tab === SelectedTab.BEST_MATCH && (
              <>
                {topCandidate.hasApplied ? (
                  <AiFillLike color={colors.blue['500']} size="20px" />
                ) : (
                  <AiOutlineLike onClick={() => handleContactRequest(topCandidate)} size="20px" />
                )}
              </>
            )}

            <ProfilePicture
              ml="10px"
              src={profileURL}
              style={{ backgroundColor: colors.orange[500], color: colors.white }}
              name={`${firstName} ${lastName}`}
              bg="transparent"
            >
              {recentlyActive && <AvatarBadge width="14px" height="14px" bg={colors.green['200']} />}
            </ProfilePicture>
          </Flex>

          <Box ml="20px">
            <Flex alignItems="center">
              <Text fontWeight={700} fontSize={{ md: '14px', lg: '16px' }} mr="4px">
                {selectedCandidate?.candidate?.isSeekerPreviouslyMonetized || selectedCandidate?.candidate?.monetized
                  ? `${selectedCandidate?.candidate?.firstName} ${selectedCandidate?.candidate?.lastName}`
                  : `${topCandidate.seeker.firstName} ${topCandidate.seeker.lastName}`}
              </Text>

              {tab === SelectedTab.ACTIVE && <TopCandidateBookmark selectedCandidate={selectedCandidate?.candidate} />}

              {topCandidate.seeker.deactivated && (
                <Tooltip label={t('candidateStatus:DEACTIVATED')} hasArrow fontSize="sm">
                  <Box as="span" ml={2}>
                    <FaUserTimes size="16px" />
                  </Box>
                </Tooltip>
              )}

              {isKnockedOutCandidate && (
                <Image
                  src="/isGoingToBeDismissed.png"
                  alt="dismiss-icon"
                  width="auto"
                  height="14px"
                  data-testid="dismissed-icon"
                  ml={1}
                  mr={1}
                />
              )}

              {topCandidate?.status === CandidateStatus.LIKED && (
                <Box
                  background={colors.green[100]}
                  color="#22543D"
                  fontWeight={700}
                  fontSize="11px"
                  h="fit-content"
                  borderRadius="14px"
                  p="2px 5px"
                  ml="4px"
                >
                  {t('businessManagement:topCandidate.hasApplied')}
                </Box>
              )}

              {topCandidate?.status === CandidateStatus.CONTACT_REQUEST_ACCEPTED && (
                <Box
                  background={colors.green[100]}
                  color="#22543D"
                  fontWeight={700}
                  fontSize="10px"
                  h="fit-content"
                  borderRadius="14px"
                  p="2px 5px"
                  ml="4px"
                >
                  {t('businessManagement:topCandidate.accepted')}
                </Box>
              )}

              {topCandidate?.status === CandidateStatus.CONTACT_REQUESTED && (
                <Box
                  background="#87CEEB"
                  color="#22543D"
                  fontWeight={700}
                  fontSize="10px"
                  h="fit-content"
                  borderRadius="14px"
                  p="2px 5px"
                  ml="4px"
                >
                  {t('businessManagement:topCandidate.requested')}
                </Box>
              )}
            </Flex>

            <Flex alignItems="center" mt={1}>
              <RiMoneyDollarCircleFill color={colors.green[700]} size="16px" />
              <Text fontSize={{ base: '12px', md: '14px' }} fontWeight={400} color={colors.neutral[600]} ml="12px">
                {topCandidate.withinRange
                  ? t('businessManagement:topCandidate.withInRange')
                  : t('businessManagement:topCandidate.aboveRange')}
              </Text>
            </Flex>

            <Text fontSize={{ base: '12px', md: '14px' }} fontWeight={400} color={colors.neutral[600]} mt={1}>
              {`${t('businessManagement:topCandidate.lastActive')} : ${moment(topCandidate.seeker.lastActivity)
                .locale(i18n.language)
                .fromNow()}`}
            </Text>
          </Box>
        </Flex>
      </GridItem>

      <GridItem>
        <Box>
          <Flex>
            <MdOutlineAccountBox size="20px" />
            <Text fontSize={{ base: '12px', md: '14px' }} fontWeight="700" ml="12px">
              {t('common:profile.experience.title')}
            </Text>
          </Flex>

          <Popover trigger="hover">
            <PopoverTrigger>
              <Box
                mt="8px"
                fontSize={{ base: '12px', md: '14px' }}
                fontWeight={400}
                background={colors.neutral[100]}
                p="8px"
                borderRadius="14px"
                color={colors.neutral[600]}
              >
                {topCandidate.experienceType === ExperienceType.SIGNIFICANT &&
                  t('businessManagement:topCandidate.significantExperience', {
                    workExperience: topCandidate.workExperience.toFixed(0),
                  })}
                {topCandidate.experienceType === ExperienceType.SOME &&
                  t('businessManagement:topCandidate.relatedExperience', {
                    workExperience: topCandidate.workExperience.toFixed(0),
                  })}
                {(topCandidate.experienceType === ExperienceType.LIMITED ||
                  (topCandidate.workExperience > 0 && topCandidate.workExperience < 1)) && (
                  <>
                    {getLimitedExperienceLabel()}
                    <Box as="span" display="block" fontSize={{ base: '12px', md: '14px' }} color={colors.neutral[500]} mt="2px">
                      {t('businessManagement:topCandidate.limitedExperienceLabel')}
                    </Box>
                  </>
                )}
                {!topCandidate.experienceType && topCandidate.workExperience === 0 && `--`}
              </Box>
            </PopoverTrigger>
            {candidateExperiences.length > 0 && (
              <PopoverContent border="1px solid #E5E7EB !important" w="380px">
                <PopoverBody>
                  {candidateExperiences.map((experience, index) => (
                    <Experience
                      key={experience.id}
                      experience={experience}
                      totalItems={candidateExperiences.length}
                      dataIndex={index}
                      displayBusiness={
                        selectedCandidate?.candidate?.monetized || selectedCandidate?.candidate?.isSeekerPreviouslyMonetized
                      }
                    />
                  ))}
                </PopoverBody>
              </PopoverContent>
            )}
          </Popover>
        </Box>
      </GridItem>

      <GridItem>
        <Box>
          <Flex>
            <FaGraduationCap size="20px" />
            <Text fontSize={{ base: '12px', md: '14px' }} fontWeight="700" ml="12px">
              {t('common:profile.education.title')}
            </Text>
          </Flex>

          {topCandidate.education ? (
            <Popover trigger="hover">
              <PopoverTrigger>
                <Box
                  mt="8px"
                  fontSize="14px"
                  fontWeight={400}
                  background={colors.neutral[100]}
                  p="8px"
                  borderRadius="14px"
                  color={colors.neutral[600]}
                >
                  {degree ? getTranslatedValue(degree.label, i18n.language) : topCandidate.education}
                </Box>
              </PopoverTrigger>
              <PopoverContent border="1px solid #E5E7EB !important" width="100%">
                <PopoverBody wordBreak="break-word" whiteSpace="normal" width="100%" overflow="auto">
                  {candidateEducations.length > 0 &&
                    candidateEducations.map((education, index) => (
                      <Education
                        key={education.id}
                        dataIndex={index}
                        education={education}
                        totalItems={candidateEducations.length}
                        educationLevels={educationLevels}
                        displaySchoolName={selectedCandidate?.candidate?.monetized}
                        width="100%"
                      />
                    ))}
                </PopoverBody>
              </PopoverContent>
            </Popover>
          ) : (
            <Box
              mt="8px"
              fontSize="14px"
              fontWeight={400}
              background={colors.neutral[100]}
              p="8px"
              borderRadius="14px"
              color={colors.neutral[600]}
            >
              --
            </Box>
          )}
        </Box>
      </GridItem>
    </Grid>
  );
};
